// Version checking utility
// Logic:
// 1. Fetch /version.json with cache-busting.
// 2. Compare with localStorage 'appVersion'. If different -> store new version and force reload (skipping for PWA standalone mode).
// 3. After reload, detect that a version change occurred via sessionStorage flag and show a notification toast message.
// 4. Export functions: checkForUpdate, showPostUpdateMessage, getCurrentVersion.

import { Toast } from 'bootstrap';

const VERSION_URL = '/version.json';
const LS_KEY = 'appVersion';
const SESSION_UPDATED_FLAG = 'appVersionUpdated';

function isStandalonePWA(){
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export async function fetchRemoteVersion(){
  const url = VERSION_URL + '?_=' + Date.now();
  const res = await fetch(url, { cache: 'no-store' });
  if(!res.ok) throw new Error('No se pudo obtener version.json');
  return res.json();
}

export function getStoredVersion(){
  return localStorage.getItem(LS_KEY);
}

export function getCurrentVersion(){
  return getStoredVersion();
}

function showToast(message, variant = 'info'){
  let container = document.getElementById('toast-container-global');
  if(!container){
    container = document.createElement('div');
    container.id = 'toast-container-global';
    container.className = 'toast-container position-fixed top-0 end-0 p-3';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
  }
  const bgClass = variant === 'success' ? 'bg-success' : variant === 'danger' ? 'bg-danger' : 'bg-info';
  const toastHtml = `
    <div class="toast align-items-center ${bgClass} text-white border-0" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;
  container.insertAdjacentHTML('beforeend', toastHtml);
  const toastElement = container.lastElementChild;
  const bsToast = new Toast(toastElement, { delay: 4000 });
  bsToast.show();
  toastElement.addEventListener('hidden.bs.toast', () => {
    toastElement.remove();
  });
}

export async function checkForUpdate({ silent = true } = {}){
  try {
    const remote = await fetchRemoteVersion();
    const remoteVersion = remote.version;
    const current = getStoredVersion();
    if(!current){
      localStorage.setItem(LS_KEY, remoteVersion);
      return { updated:false, current: remoteVersion };
    }
    if(current !== remoteVersion){
      localStorage.setItem(LS_KEY, remoteVersion);
      sessionStorage.setItem(SESSION_UPDATED_FLAG, remoteVersion);
      if(!isStandalonePWA()){
        if(!silent) console.log('[versioning] Nueva versión detectada', current, '->', remoteVersion);
        if(navigator.serviceWorker && navigator.serviceWorker.controller){
          navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
          setTimeout(() => window.location.reload(), 400);
        } else {
          window.location.reload();
        }
      } else {
        showUpdateMessage(remoteVersion, current);
      }
      return { updated:true, from: current, to: remoteVersion };
    }
    return { updated:false, current };
  } catch (e){
    if(!silent) console.warn('[versioning] Error chequeando versión', e);
    return { error: e.message };
  }
}

export function showUpdateMessage(newVersion, oldVersion){
  const text = `Se actualizó de la versión ${oldVersion || '—'} a ${newVersion}.`;
  try {
    showToast(text, 'info');
  } catch(_){
    alert(`Aplicación actualizada: ${text}`);
  }
}

export function showPostUpdateMessage(){
  const flag = sessionStorage.getItem(SESSION_UPDATED_FLAG);
  if(flag){
    const oldVersion = sessionStorage.getItem(SESSION_UPDATED_FLAG + '_from');
    sessionStorage.removeItem(SESSION_UPDATED_FLAG);
    sessionStorage.removeItem(SESSION_UPDATED_FLAG + '_from');
    showUpdateMessage(flag, oldVersion);
  }
}

export function startPeriodicChecks(intervalMs = 5 * 60 * 1000){
  setInterval(() => {
    checkForUpdate({ silent: true });
  }, intervalMs);
}
